import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import { getLogEntries, upsertLogEntry, deleteLogEntry, getMaintenanceChecks, upsertMaintenanceCheck, deleteMaintenanceCheck, getBoatSettings, upsertBoatSettings, getServiceRecords, upsertServiceRecord, getFuelLog, upsertFuelEntry, deleteFuelEntry, getVoyages, upsertVoyage, deleteVoyage, setEntriesVoyage } from './lib/db';
import Masthead from './components/Masthead';
import NavBar from './components/NavBar';
import BoatSwitcher from './components/BoatSwitcher';
import { getBoats } from './lib/db';
import Toast from './components/Toast';
import LogbookTab from './components/LogbookTab';
import MaintenanceTab from './components/MaintenanceTab';
import VoyageModal from './components/VoyageModal';
import EntryModal from './components/EntryModal';
import SettingsModal from './components/SettingsModal';
import BoatTab from './components/BoatTab';
import CrewTab from './components/CrewTab';
import AddSheet from './components/AddSheet';
import { engineHours, genHours, hullNm, latestReadings } from './lib/utils';

const DEFAULT_SETTINGS = {
  engine_baseline: 2940, engine_current: null,
  gen_baseline: 1130,    gen_current: null,
  hull_nm_baseline: 12934,
  boat_name: 'Pardon My French', boat_year: '2006',
  boat_make: 'Jeanneau', boat_model: 'Sun Odyssey 45',
  sail_number: '2590', phrf: '144', hailing_port: 'Orcas, WA',
  mmsi: '', hin: 'JS045H506', tow_us: '',
  home_marina: 'Elliott Bay Marina', slip: 'D-4',
  home_lat: 47.6234, home_lon: -122.3927,
  tank1_cap: 90, tank2_cap: 38, water_aft_cap: 240,
  crew_list: [{ id:'1', name:'Frederick', role:'Captain' }],
  theme: 'system', default_captain: 'Frederick',
  distance_unit: 'nm', temp_unit: 'F',
};

export default function App() {
  const [tab, setTab]               = useState('logbook');
  const [boats, setBoats]           = useState([]);
  const [boatsErr, setBoatsErr]     = useState(null);
  const [activeBoat, setActiveBoat] = useState('all');   // 'all' | boat id
  const [entries, setEntries]       = useState([]);
  const [checks, setChecks]         = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [fuelLog, setFuelLog]           = useState([]);
  const [voyages, setVoyages]           = useState([]);
  const [showVoyage, setShowVoyage]     = useState(false);
  const [editVoyage, setEditVoyage]     = useState(null);
  const [fuelRecords, setFuelRecords]       = useState([]);
  const [settings, setSettings]     = useState(DEFAULT_SETTINGS);
  const [loading, setLoading]       = useState(true);
  const [dbError, setDbError]       = useState(null);
  const [showEntry, setShowEntry]   = useState(false);
  const [showAdd, setShowAdd]       = useState(false);
  const [maintAction, setMaintAction] = useState(null); // one-shot: 'fuel' | 'check'
  const [showSettings, setShowSettings] = useState(false);
  const [editEntry, setEditEntry]   = useState(null);
  const [toast, setToast]           = useState({ msg:'', type:'ok' });

  const notify = useCallback((msg, type='ok') => setToast({ msg, type }), []);

  // Load all on mount
  useEffect(() => {
    const url = process.env.REACT_APP_SUPABASE_URL;
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!url || !key) { setDbError('Supabase env vars not set'); setLoading(false); return; }
    Promise.all([getLogEntries(), getMaintenanceChecks(), getBoatSettings(), getServiceRecords(), getFuelLog(), getVoyages()])
      .then(([logs, chks, s, svcs, fuels, voys]) => {
        setEntries(logs);
        setChecks(chks);
        if (s) setSettings({ ...DEFAULT_SETTINGS, ...s });
        setServiceRecords(svcs);
        setFuelLog(fuels);
        setVoyages(voys);
        setLoading(false);
      })
      .catch(e => { setDbError(e.message); setLoading(false); });
    // Boats load on their own so a boats issue can't break the rest of the app,
    // and any error is surfaced (not silently swallowed) to make debugging easy.
    getBoats()
      .then(bts => setBoats(bts || []))
      .catch(e => setBoatsErr(e.message || String(e)));
  }, []);

  // Active boat object ('all' has no row); its color themes the app.
  const currentBoat = boats.find(b => b.id === activeBoat) || null;

  // Filter to the active boat; 'all' shows the whole fleet. A lookup map lets
  // cards render a colored boat chip in All view.
  const boatsById = React.useMemo(() => {
    const m = {}; boats.forEach(b => { m[b.id] = b; }); return m;
  }, [boats]);
  const viewEntries = React.useMemo(
    () => activeBoat === 'all' ? entries : entries.filter(e => e.boat_id === activeBoat),
    [entries, activeBoat]
  );
  const viewVoyages = React.useMemo(
    () => activeBoat === 'all' ? voyages : voyages.filter(v => v.boat_id === activeBoat),
    [voyages, activeBoat]
  );

  // Reskin the app when the active boat changes: the boat's signature color
  // drives the accent, and its bg/navy tint the surface. 'all' resets to base.
  useEffect(() => {
    const root = document.documentElement;
    if (currentBoat) {
      root.style.setProperty('--accent', currentBoat.color);
      if (currentBoat.bg) root.style.setProperty('--bg', currentBoat.bg);
      if (currentBoat.navy) root.style.setProperty('--navy', currentBoat.navy);
    } else {
      root.style.setProperty('--accent', '#5B7A93');
      root.style.removeProperty('--bg');    // fall back to stylesheet defaults
      root.style.removeProperty('--navy');
    }
  }, [currentBoat]);

  // Derived — engine/gen are odometers (highest reading wins); hull nm is cumulative.
  const currentEngHrs = engineHours(entries, settings) || 2940;
  const currentGenHrs = genHours(entries, settings) || 1130;
  const currentHullNm = hullNm(entries, settings);
  const prevEntry = [...entries].filter(e=>!e.guest_sailing).sort((a,b)=>new Date(b.date)-new Date(a.date))[0];
  const carryReadings = latestReadings(entries, settings);

  // Handlers
  const handleSaveEntry = useCallback(async (raw) => {
    try {
      // Stamp the boat: keep an existing entry's boat, else the active boat.
      // In 'all' view a new entry falls back to the first boat (or 'pmf').
      const fallbackBoat = activeBoat !== 'all' ? activeBoat : (boats[0]?.id || 'pmf');
      const withBoat = { ...raw, boat_id: raw.boat_id || fallbackBoat };
      const saved = await upsertLogEntry(withBoat);
      setEntries(prev => { const idx=prev.findIndex(e=>e.id===saved.id); return idx>=0?prev.map(e=>e.id===saved.id?saved:e):[saved,...prev]; });

      // Sync: if the entry ticked any engine check, mirror it into the
      // Maintenance tab as a linked check (one per entry, id derived from the
      // entry id so re-saving updates rather than duplicates).
      const anyCheck = saved.chk_oil || saved.chk_strainer || saved.chk_bilge;
      if (anyCheck) {
        const chk = {
          id: 'chkfromlog_' + saved.id,
          date: saved.date,
          performed_by: saved.captain || 'Frederick',
          from_log: saved.id,
          notes: [saved.destination ? `Logged underway · ${saved.destination}` : 'Logged underway', saved.chk_bilge ? 'bilge checked' : ''].filter(Boolean).join(' · '),
          engine: {
            oil_checked: !!saved.chk_oil,
            oil_qty: saved.oil_qty || null,
            oil_hrs: saved.oil_eng_hrs || saved.engine_end || null,
            strainer_cleaned: !!saved.chk_strainer,
          },
        };
        try {
          const savedChk = await upsertMaintenanceCheck(chk);
          setChecks(prev => { const i=prev.findIndex(c=>c.id===savedChk.id); return i>=0?prev.map(c=>c.id===savedChk.id?savedChk:c):[savedChk,...prev]; });
        } catch { /* non-fatal: entry already saved */ }
      }

      notify('Entry saved ✓');
    } catch(e) { notify(e.message,'error'); throw e; }
  }, [notify, activeBoat, boats]);

  const handleDeleteEntry = useCallback(async (entry) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await deleteLogEntry(entry.id); setEntries(prev=>prev.filter(e=>e.id!==entry.id)); notify('Entry deleted'); }
    catch(e) { notify(e.message,'error'); }
  }, [notify]);

  // ── Voyages ──
  const handleSaveVoyage = useCallback(async ({ voyage, assignEntryIds }) => {
    try {
      const saved = await upsertVoyage(voyage);
      setVoyages(prev => { const i=prev.findIndex(v=>v.id===saved.id); return i>=0?prev.map(v=>v.id===saved.id?saved:v):[saved,...prev]; });
      if (assignEntryIds && assignEntryIds.length) {
        await setEntriesVoyage(assignEntryIds, saved.id);
        setEntries(prev => prev.map(e => assignEntryIds.includes(e.id) ? { ...e, voyage_id: saved.id } : e));
      }
      notify('Voyage saved ✓');
    } catch(e) { notify(e.message,'error'); throw e; }
  }, [notify]);

  const handleDeleteVoyage = useCallback(async (voyage) => {
    if (!window.confirm('Delete this voyage? Its entries stay in the log, just un-grouped.')) return;
    try {
      await deleteVoyage(voyage.id);
      setVoyages(prev => prev.filter(v => v.id !== voyage.id));
      setEntries(prev => prev.map(e => e.voyage_id === voyage.id ? { ...e, voyage_id: null } : e));
      notify('Voyage deleted');
    } catch(e) { notify(e.message,'error'); }
  }, [notify]);

  const handleSaveCheck = useCallback(async (raw) => {
    try {
      const saved = await upsertMaintenanceCheck(raw);
      setChecks(prev => { const idx=prev.findIndex(e=>e.id===saved.id); return idx>=0?prev.map(e=>e.id===saved.id?saved:e):[saved,...prev]; });
      notify('Check saved ✓');
    } catch(e) { notify(e.message,'error'); throw e; }
  }, [notify]);

  const handleDeleteCheck = useCallback(async (entry) => {
    try { await deleteMaintenanceCheck(entry.id); setChecks(prev=>prev.filter(e=>e.id!==entry.id)); notify('Deleted'); }
    catch(e) { notify(e.message,'error'); }
  }, [notify]);

  const handleSaveServiceRecord = useCallback(async (record) => {
    try {
      const saved = await upsertServiceRecord(record);
      setServiceRecords(prev => { const idx=prev.findIndex(r=>r.id===saved.id); return idx>=0?prev.map(r=>r.id===saved.id?saved:r):[saved,...prev]; });
    } catch(e) { notify(e.message,'error'); throw e; }
  }, [notify]);

  const handleSaveSettings = useCallback(async (newSettings) => {
    try {
      const saved = await upsertBoatSettings(newSettings);
      setSettings({ ...DEFAULT_SETTINGS, ...saved });
      notify('Settings saved ✓');
    } catch(e) { notify(e.message,'error'); }
  }, [notify]);


  const handleUploadDoc = useCallback(async (file, docType) => {
    // Upload to Supabase Storage
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const sb = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
      const ext = file.name.split('.').pop();
      const path = `docs/${docType}_${Date.now()}.${ext}`;
      const { error } = await sb.storage.from('boat-docs').upload(path, file, { upsert:true });
      if (error) throw error;
      const { data } = sb.storage.from('boat-docs').getPublicUrl(path);
      return data.publicUrl;
    } catch(e) {
      console.error('Upload failed:', e.message);
      return null;
    }
  }, []);

  const handleSaveFuel = useCallback(async (raw) => {
    try {
      const saved = await upsertFuelEntry(raw);
      setFuelLog(prev => { const idx=prev.findIndex(e=>e.id===saved.id); return idx>=0?prev.map(e=>e.id===saved.id?saved:e):[saved,...prev]; });
      notify('Fuel fill saved ✓');
    } catch(e) { notify(e.message,'error'); throw e; }
  }, [notify]);

  const handleDeleteFuel = useCallback(async (entry) => {
    try { await deleteFuelEntry(entry.id); setFuelLog(prev=>prev.filter(e=>e.id!==entry.id)); notify('Deleted'); }
    catch(e) { notify(e.message,'error'); }
  }, [notify]);


  const [logbookKey, setLogbookKey] = useState(0);
  const handleTabSelect = useCallback((id) => {
    if (id === 'add') { setShowAdd(true); return; }
    // Tapping Logbook while already on it (e.g. from an open detail) resets it.
    if (id === 'logbook' && tab === 'logbook') setLogbookKey(k => k + 1);
    setTab(id);
  }, [tab]);

  // The + sheet routes to the right form. Fuel and maintenance forms live
  // inside MaintenanceTab, so we switch tabs and hand it a one-shot action.
  const handleAddPick = useCallback((choice) => {
    if (choice === 'log') { setEditEntry(null); setShowEntry(true); return; }
    if (choice === 'fuel') { setTab('maintenance'); setMaintAction('fuel'); return; }
    if (choice === 'maintenance') { setTab('maintenance'); setMaintAction('check'); return; }
  }, []);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12 }}>
      <div style={{ fontFamily:"'Caveat',cursive", fontSize:42, color:'var(--navy)' }}>Pardon My French</div>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.2em', color:'var(--gold)' }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ minHeight:'100dvh', background:'var(--bg)' }}>
      <Masthead/>
      {boats.length > 0 && (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <BoatSwitcher boats={boats} activeId={activeBoat} onSelect={setActiveBoat} />
        </div>
      )}
      <NavBar active={tab} onSelect={handleTabSelect} width={tab==='logbook' ? 1440 : 900}/>

      {boatsErr && (
        <div style={{ background:'rgba(184,134,11,.12)', borderBottom:'1px solid rgba(184,134,11,.35)', padding:'8px 20px', fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--gold-deep, #8B6508)', textAlign:'center' }}>
          boats not loaded — {boatsErr}
        </div>
      )}

      {dbError && (
        <div style={{ background:'rgba(192,57,45,.1)', borderBottom:'1px solid rgba(192,57,45,.3)', padding:'12px 20px', fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--red)', textAlign:'center' }}>
          ⚠ {dbError}
        </div>
      )}

      <div style={{ maxWidth: 1000, margin:'0 auto', padding:'24px 18px', paddingBottom:'calc(90px + env(safe-area-inset-bottom))' }}>
        {tab==='logbook'     && <LogbookTab key={logbookKey} entries={viewEntries} fuelLog={fuelLog} settings={settings} onEdit={e=>{setEditEntry(e);setShowEntry(true);}} onDelete={handleDeleteEntry} voyages={viewVoyages} onNewVoyage={()=>{setEditVoyage(null);setShowVoyage(true);}} onEditVoyage={v=>{setEditVoyage(v);setShowVoyage(true);}} onDeleteVoyage={handleDeleteVoyage} allBoats={activeBoat==='all'} boatsById={boatsById}/>}
        {tab==='maintenance' && <MaintenanceTab openAction={maintAction} onActionHandled={()=>setMaintAction(null)} checks={checks} serviceRecords={serviceRecords} fuelLog={fuelLog} currentEngHrs={currentEngHrs} currentGenHrs={currentGenHrs} onSaveCheck={handleSaveCheck} onDeleteCheck={handleDeleteCheck} onSaveServiceRecord={handleSaveServiceRecord} onSaveFuel={handleSaveFuel} onDeleteFuel={handleDeleteFuel}/>}
        {tab==='stats'       && <CrewTab entries={viewEntries} settings={settings} allBoats={activeBoat==='all'} boatsById={boatsById}/>}
        {tab==='boat'        && <BoatTab settings={settings} entries={viewEntries} engHrs={currentEngHrs} genHrs={currentGenHrs} hullNm={currentHullNm} onSettings={()=>setShowSettings(true)}/>}
      </div>

      <VoyageModal isOpen={showVoyage} onClose={()=>setShowVoyage(false)} onSave={handleSaveVoyage} editVoyage={editVoyage} allEntries={entries}/>

      <EntryModal
        isOpen={showEntry}
        onClose={() => { setShowEntry(false); setEditEntry(null); }}
        onSave={handleSaveEntry}
        prevEntry={prevEntry}
        carryReadings={carryReadings}
        entries={entries}
        editEntry={editEntry}
        defaultCaptain={settings.default_captain || 'Frederick'}
        crewList={settings.crew_list || []}
        homeLat={settings.home_lat || 47.6234}
        homeLon={settings.home_lon || -122.3927}
        engineBaseline={currentEngHrs}
        genBaseline={currentGenHrs}
      />

      <AddSheet isOpen={showAdd} onClose={()=>setShowAdd(false)} onPick={handleAddPick}/>

      <SettingsModal entries={entries}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={handleSaveSettings}
        onUploadDoc={handleUploadDoc}
      />

      <Toast msg={toast.msg} type={toast.type} onDone={() => setToast({ msg:'', type:'ok' })}/>
    </div>
  );
}
